import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { Link } from "react-router-dom";
import { getDownloadURL, ref as storageRef } from "firebase/storage";

import CountryFlag from "../../components/common/CountryFlag";
import { db, storage } from "../../firebase";
import { defaultAvatarByGender } from "../../utils/defaultAvatar";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

const GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(440px, 1fr))",
  gap: 16,
};

const CARD = {
  border: "1px solid #253654",
  borderRadius: 14,
  background: "linear-gradient(180deg, #12213a, #101b30)",
  padding: 18,
  minWidth: 0,
};

const SECTION_LABEL = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "#7d8fb5",
  marginBottom: 10,
  paddingBottom: 6,
  borderBottom: "1px solid #1e2f4d",
};

const ROW = {
  display: "grid",
  gridTemplateColumns: "150px 1fr",
  gap: "8px 14px",
  fontSize: 13,
  alignItems: "start",
  marginBottom: 6,
};

const LABEL = { color: "#9fb2d8" };
const VALUE = { color: "#e8eefc", wordBreak: "break-word" };
const MONO = {
  ...VALUE,
  fontFamily: 'Consolas, "Courier New", monospace',
  fontSize: 12,
};

const LINK_BTN = {
  display: "inline-block",
  padding: "6px 12px",
  borderRadius: 8,
  border: "1px solid #37538f",
  background: "#182946",
  color: "#c7d7ff",
  fontSize: 12,
  fontWeight: 600,
  textDecoration: "none",
  marginRight: 8,
};

function ResolvedAvatar({ raw, imgStyle, gender }) {
  const fb = defaultAvatarByGender(gender);
  const [src, setSrc] = useState(fb);

  useEffect(() => {
    let cancelled = false;
    const fallback = defaultAvatarByGender(gender);
    const v = String(raw || "").trim();
    if (!v) {
      setSrc(fallback);
      return () => {
        cancelled = true;
      };
    }
    if (/^https?:\/\//i.test(v)) {
      setSrc(v);
      return () => {
        cancelled = true;
      };
    }
    let path = v;
    if (v.startsWith("gs://")) {
      const m = v.match(/^gs:\/\/[^/]+\/(.+)$/);
      path = m ? m[1] : "";
    }
    if (!path) {
      setSrc(fallback);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const url = await getDownloadURL(storageRef(storage, path));
        if (!cancelled) setSrc(url);
      } catch (e) {
        console.error("ResolvedAvatar getDownloadURL:", path, e);
        if (!cancelled) setSrc(fallback);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [raw, gender]);

  return (
    <img
      src={src}
      alt=""
      style={imgStyle}
      onError={(e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = defaultAvatarByGender(gender);
      }}
    />
  );
}

function formatAnyTime(ts, msFallback) {
  if (ts && typeof ts.toDate === "function") {
    try {
      return ts.toDate().toLocaleString();
    } catch {
      /* ignore */
    }
  }
  const ms = Number(msFallback);
  if (Number.isFinite(ms) && ms > 0) return new Date(ms).toLocaleString();
  return "—";
}

function stringifyPreview(obj) {
  if (obj == null || typeof obj !== "object") return String(obj ?? "—");
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return "—";
  }
}

export default function PaidWithdrawalsPage() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "withdrawals"), orderBy("createdAt", "desc"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
          .filter((row) =>
            ["paid", "rejected", "cancelled"].includes(
              String(row?.status || "").trim().toLowerCase()
            )
          )
          .map((row) => {
            const snap = row.userSnapshot || {};
            const photoRaw = String(
              row.profileImage || row.photoURL || snap.photoURL || ""
            ).trim();
            return {
              id: row.id,
              uid: String(row?.uid || "—"),
              status: String(row?.status || "—"),
              method: String(row?.method || "—"),
              amountTokens: toNumber(row?.amountTokens, 0),
              requestedUsdPreview: toNumber(row?.requestedUsdPreview, 0),
              createdAtMs: Number(row?.createdAtMs || 0),
              createdAt: row.createdAt,
              reviewedAt: row.reviewedAt,
              reviewedAtMs: Number(row?.reviewedAtMs || 0),
              paidAt: row.paidAt,
              paidAtMs: Number(row?.paidAtMs || 0),
              paidBy: String(row?.paidBy ?? ""),
              adminNote: String(row?.adminNote ?? ""),
              paymentReference: String(row?.paymentReference ?? ""),
              payoutPreview: row.payoutPreview,
              photoRaw,
              userSnapshot: {
                displayName: String(snap.displayName || "—"),
                country: String(snap.country || "—"),
                gender: String(snap.gender || ""),
              },
            };
          });

        setRequests(list);
        setLoading(false);
      },
      (error) => {
        console.error("PaidWithdrawalsPage snapshot error:", error);
        setRequests([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const nf = new Intl.NumberFormat();

  return (
    <div className="admin1-dashboard">
      <header className="admin1-dashboard__header">
        <h1 className="admin1-dashboard__title">{t("admin.withdrawals.paid_title", { defaultValue: "Paid Withdrawals" })}</h1>
        <p className="admin1-dashboard__subtitle">
          Read-only list for paid, rejected, and cancelled withdrawals.
        </p>
      </header>

      {loading ? (
        <div className="admin1-state-card">Loading…</div>
      ) : requests.length === 0 ? (
        <div className="admin1-state-card">{t("admin.withdrawals.no_paid", { defaultValue: "No paid withdrawals" })}</div>
      ) : (
        <section style={GRID}>
          {requests.map((request) => (
            <article
              key={request.id}
              id={`paid-withdraw-${request.id}`}
              style={CARD}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "flex-start",
                  marginBottom: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", gap: 12, minWidth: 0 }}>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 10,
                      overflow: "hidden",
                      flexShrink: 0,
                      border: "1px solid #253654",
                      background: "#0f1a2f",
                    }}
                  >
                    <ResolvedAvatar
                      raw={request.photoRaw}
                      gender={request.userSnapshot?.gender}
                      imgStyle={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#f3f6ff",
                        marginBottom: 4,
                      }}
                    >
                      {request.userSnapshot.displayName}
                    </div>
                    <div style={{ ...MONO, marginBottom: 6 }}>
                      {request.uid}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#9fb2d8",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <CountryFlag countryCode={request.userSnapshot.country} />
                    </div>
                  </div>
                </div>
                <div>
                  <Link
                    to={`/user/${encodeURIComponent(request.uid)}`}
                    style={LINK_BTN}
                  >
                    View User
                  </Link>
                  <a href={`#paid-withdraw-${request.id}`} style={LINK_BTN}>
                    View Details
                  </a>
                </div>
              </div>

              <div style={SECTION_LABEL}>{t("admin.withdrawals.withdrawal", { defaultValue: "Withdrawal" })}</div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.withdrawals.withdraw_id", { defaultValue: "withdrawId" })}</span>
                <span style={MONO}>{request.id}</span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("common.status", { defaultValue: "status" })}</span>
                <span style={VALUE}>{request.status}</span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.withdrawals.method", { defaultValue: "method" })}</span>
                <span style={VALUE}>{request.method}</span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.fields.created_at", { defaultValue: "createdAt" })}</span>
                <span style={VALUE}>
                  {formatAnyTime(request.createdAt, request.createdAtMs)}
                </span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.fields.reviewed_at", { defaultValue: "reviewedAt" })}</span>
                <span style={VALUE}>
                  {formatAnyTime(request.reviewedAt, request.reviewedAtMs)}
                </span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.fields.paid_at", { defaultValue: "paidAt" })}</span>
                <span style={VALUE}>
                  {formatAnyTime(request.paidAt, request.paidAtMs)}
                </span>
              </div>

              <div style={{ ...SECTION_LABEL, marginTop: 12 }}>{t("admin.finance.amount", { defaultValue: "Amount" })}</div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.finance.amount_tokens", { defaultValue: "amountTokens" })}</span>
                <span style={{ ...VALUE, fontWeight: 700 }}>
                  {nf.format(request.amountTokens)}
                </span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.finance.requested_usd_preview", { defaultValue: "requestedUsdPreview" })}</span>
                <span style={VALUE}>
                  {request.requestedUsdPreview.toFixed(2)} USD
                </span>
              </div>

              <div style={{ ...SECTION_LABEL, marginTop: 12 }}>{t("admin.withdrawals.payout", { defaultValue: "Payout" })}</div>
              <pre
                style={{
                  margin: 0,
                  padding: 10,
                  borderRadius: 8,
                  background: "#0b1426",
                  border: "1px solid #1e2f4d",
                  color: "#b8c8e8",
                  fontSize: 11,
                  lineHeight: 1.45,
                  overflow: "auto",
                  maxHeight: 160,
                }}
              >
                {stringifyPreview(request.payoutPreview)}
              </pre>

              <div style={{ ...SECTION_LABEL, marginTop: 12 }}>{t("admin.title", { defaultValue: "Admin" })}</div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.fields.paid_by", { defaultValue: "paidBy" })}</span>
                <span style={MONO}>{request.paidBy || "—"}</span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.fields.payment_reference", { defaultValue: "paymentReference" })}</span>
                <span style={MONO}>{request.paymentReference || "—"}</span>
              </div>
              <div style={ROW}>
                <span style={LABEL}>{t("admin.fields.admin_note", { defaultValue: "adminNote" })}</span>
                <span style={VALUE}>{request.adminNote || "—"}</span>
              </div>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
